// Formules d'abonnement (spec § 3) : 1 pays, 1 zone ou le monde, avec engagement annuel ou par trimestre (3 mois
// minimum). Chaque formule montre ce que la base recense sur le territoire choisi ; la demande alerte le
// gestionnaire des abonnements, qui fixe l'offre (le paiement viendra plus tard). Prix et crédits viennent de `subscription_plans`.
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check } from "lucide-react";
import {
  useGetIdentity,
  useGetOne,
  useLocaleState,
  useNotify,
  useTranslate,
} from "ra-core";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { cn } from "@/lib/utils";

import { getSupabaseClient } from "../providers/supabase/supabase";
import type {
  Commitment,
  Sale,
  SubscriptionPlan,
  SubscriptionRequest,
} from "../types";
import { countryName, formatDay } from "./projectUtils";
import {
  type CoverageRow,
  type Zone,
  coverageOf,
  subscriptionLabel,
  useCoverage,
  useZones,
} from "./territories";
import { type AccountCurrency, formatMoney } from "./accountCurrency";
import { useAccountCurrency } from "./CurrencySelector";
import { useCreditPrices } from "./creditPrices";

const usePlans = () =>
  useQuery({
    queryKey: ["subscription_plans"],
    queryFn: async (): Promise<SubscriptionPlan[]> => {
      const { data, error } = await getSupabaseClient()
        .from("subscription_plans")
        .select("*")
        .order("position");
      if (error) throw error;
      return data ?? [];
    },
    staleTime: Infinity,
  });

const usePendingRequest = (sale?: Sale) =>
  useQuery({
    queryKey: ["subscription_requests", "pending", sale?.id],
    enabled: !!sale?.partner,
    queryFn: async (): Promise<SubscriptionRequest | null> => {
      const { data, error } = await getSupabaseClient()
        .from("subscription_requests")
        .select("*")
        .eq("sales_id", sale!.id)
        .eq("status", "pending")
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

export const Pricing = () => {
  const translate = useTranslate();
  const { identity } = useGetIdentity();
  const { data: sale } = useGetOne<Sale>(
    "sales",
    { id: identity?.id ?? 0 },
    { enabled: !!identity?.id },
  );
  const { data: plans } = usePlans();
  const { data: rows } = useCoverage();
  const { data: zones } = useZones();
  const { data: pending } = usePendingRequest(sale);
  const [chosen, setCommitment] = useState<Commitment>("annual");
  const { currency } = useAccountCurrency();
  if (!sale || !plans || !rows || !zones) return null;

  // Facturation annuelle d'abord : le choix « sans engagement » n'apparaît que si une formule l'ouvre.
  const monthlyOpen = plans.some((plan) => plan.monthly_available);
  const commitment = monthlyOpen ? chosen : "annual";

  // Seul un partenaire demande un abonnement ; l'équipe voit la page telle que les partenaires la voient.
  const canRequest = !!sale.partner && !sale.administrator && !pending;

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold">
            {translate("resources.projects.pricing.title")}
          </h3>
          <p className="text-sm text-muted-foreground">
            {translate("resources.projects.pricing.intro")}
          </p>
        </div>
        {monthlyOpen && (
          <ToggleGroup
            type="single"
            variant="outline"
            size="sm"
            value={commitment}
            onValueChange={(value) =>
              value && setCommitment(value as Commitment)
            }
          >
            <ToggleGroupItem value="annual" className="px-3">
              {translate("resources.projects.pricing.annual")}
            </ToggleGroupItem>
            <ToggleGroupItem value="monthly" className="px-3">
              {translate("resources.projects.pricing.monthly")}
            </ToggleGroupItem>
          </ToggleGroup>
        )}
      </div>
      {pending && <PendingRequest request={pending} />}
      {!sale.partner && (
        <p className="text-sm text-muted-foreground">
          {translate("resources.projects.pricing.team_preview")}
        </p>
      )}
      <div className="grid gap-4 md:grid-cols-3">
        {plans.map((plan) => (
          <PlanCard
            key={plan.code}
            plan={plan}
            currency={currency}
            commitment={commitment}
            monthlyOpen={monthlyOpen}
            rows={rows}
            zones={zones}
            canRequest={canRequest}
          />
        ))}
      </div>
      <p className="text-xs text-muted-foreground">
        {translate("resources.projects.pricing.footnote")}
      </p>
    </section>
  );
};

const PlanCard = ({
  plan,
  currency,
  commitment,
  monthlyOpen,
  rows,
  zones,
  canRequest,
}: {
  plan: SubscriptionPlan;
  currency: AccountCurrency;
  commitment: Commitment;
  monthlyOpen: boolean;
  rows: CoverageRow[];
  zones: Zone[];
  canRequest: boolean;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const prices = useCreditPrices();
  const [locale = "fr"] = useLocaleState();
  // Par défaut, le pays et la zone les mieux couverts.
  const [country, setCountry] = useState(
    () =>
      [...rows].sort(
        (a, b) => Number(b.projects_30d) - Number(a.projects_30d),
      )[0]?.country ?? "",
  );
  const [zone, setZone] = useState(zones[0]?.code ?? "");
  const [sending, setSending] = useState(false);
  const t = (key: string, options?: object) =>
    translate(`resources.projects.pricing.${key}`, options);

  const world = plan.code === "world";
  // Grille en euros ou en dollars, fixes toutes les deux (spec § 3) : pas de conversion au jour le jour.
  const [annual, monthly] =
    currency === "USD"
      ? [plan.price_annual_usd, plan.price_monthly_usd]
      : [plan.price_annual_eur, plan.price_monthly_eur];
  const price = commitment === "annual" ? annual : monthly;
  const saving = Math.round((1 - annual / monthly) * 100);
  const money = (value: number) => formatMoney(value, currency, locale, false);
  const coverage = coverageOf(
    rows,
    zones,
    world
      ? "world"
      : plan.code === "zone"
        ? { zones: [zone], countries: [] }
        : { zones: [], countries: [country] },
  );

  const request = async () => {
    setSending(true);
    const { error } = await getSupabaseClient()
      .from("subscription_requests")
      .insert({
        plan: plan.code,
        commitment,
        country: plan.code === "country" ? country : null,
        zone: plan.code === "zone" ? zone : null,
      });
    setSending(false);
    if (error) {
      notify(error.message, { type: "error" });
      return;
    }
    notify(t("sent"));
    queryClient.invalidateQueries({ queryKey: ["subscription_requests"] });
  };

  return (
    <Card className={cn("relative", world && "border-primary shadow-md")}>
      {world && (
        <Badge className="absolute -top-2.5 left-6">{t("complete")}</Badge>
      )}
      <CardContent className="flex flex-col gap-4 h-full">
        <div>
          <div className="font-semibold">{t(`plans.${plan.code}.name`)}</div>
          <p className="text-sm text-muted-foreground">
            {t(`plans.${plan.code}.tagline`)}
          </p>
        </div>
        <div>
          <div className="flex items-baseline gap-1">
            <span className="text-3xl font-semibold tabular-nums">
              {money(price)}
            </span>
            <span className="text-sm text-muted-foreground">
              {t("per_month")}
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            {commitment === "monthly"
              ? t("monthly_detail", { total: money(price * 3) })
              : monthlyOpen
                ? t("annual_detail", { total: money(price * 12), saving })
                : t("annual_billed", { total: money(price * 12) })}
          </p>
        </div>

        {plan.code === "country" && (
          <Select value={country} onValueChange={setCountry}>
            <SelectTrigger className="w-full" size="sm">
              <SelectValue placeholder={t("choose_country")} />
            </SelectTrigger>
            <SelectContent>
              {[...rows]
                .sort((a, b) => Number(b.projects_30d) - Number(a.projects_30d))
                .map((row) => (
                  <SelectItem key={row.country} value={row.country}>
                    {countryName(row.country, locale)} ({row.projects_30d})
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        )}
        {plan.code === "zone" && (
          <Select value={zone} onValueChange={setZone}>
            <SelectTrigger className="w-full" size="sm">
              <SelectValue placeholder={t("choose_zone")} />
            </SelectTrigger>
            <SelectContent>
              {zones.map((item) => (
                <SelectItem key={item.code} value={item.code}>
                  {translate(`resources.projects.zones.${item.code}`)} (
                  {
                    coverageOf(rows, zones, {
                      zones: [item.code],
                      countries: [],
                    }).projects_30d
                  }
                  )
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        <div className="rounded-md bg-muted px-3 py-2 text-sm">
          {t("coverage", {
            smart_count: coverage.projects_30d,
            high: coverage.high_score_30d,
            countries: coverage.countries,
          })}
        </div>

        <ul className="flex flex-col gap-1.5 text-sm">
          {[
            t(`plans.${plan.code}.scope`),
            t("feature_deals"),
            t("feature_credits", {
              smart_count: plan.monthly_credits,
              email: prices?.email ?? "…",
              mobile: prices?.mobile ?? "…",
            }),
            t("feature_assistant"),
            t("feature_alerts"),
          ].map((feature) => (
            <li key={feature} className="flex gap-2">
              <Check className="size-4 mt-0.5 shrink-0 text-primary" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>

        <Button
          className="mt-auto"
          variant={world ? "default" : "outline"}
          disabled={!canRequest || sending}
          onClick={request}
        >
          {t("request")}
        </Button>
      </CardContent>
    </Card>
  );
};

const PendingRequest = ({ request }: { request: SubscriptionRequest }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [locale = "fr"] = useLocaleState();
  const cancel = async () => {
    const { error } = await getSupabaseClient()
      .from("subscription_requests")
      .update({ status: "cancelled" })
      .eq("id", request.id);
    if (error) notify(error.message, { type: "error" });
    queryClient.invalidateQueries({ queryKey: ["subscription_requests"] });
  };
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-primary/40 bg-primary/5 px-4 py-3 text-sm">
      <span>
        {translate("resources.projects.pricing.pending", {
          date: formatDay(request.created_at, locale),
          label: subscriptionLabel(request, locale, translate),
        })}
      </span>
      <Button size="sm" variant="ghost" onClick={cancel}>
        {translate("resources.projects.pricing.cancel")}
      </Button>
    </div>
  );
};
